import { useMemo, useState } from 'react';
import type { LeadRecord, ProvisioningStep } from '@flashpoint/site-dna';
import { provisionLeadStream, type ProvisionStreamEvent } from '../../lib/orchestratorClient';
import {
  DeployPreviewStatus,
  ProvisionStreamLine,
  deriveDeployPhase,
  lastCloudflareHeadSha,
} from '../../components/DeployPreviewStatus';
import { fullDateTime, relativeTime } from '../../lib/dateFmt';
import { PreviewLink } from './PreviewLink';

interface Props {
  lead: LeadRecord;
}

export function ProvisioningTab({ lead }: Props) {
  const { provisioning } = lead;
  const [running, setRunning] = useState(false);
  const [lastErr, setLastErr] = useState<string | null>(null);
  const [streamLog, setStreamLog] = useState<ProvisionStreamEvent[]>([]);
  const deployPhase = useMemo(
    () => deriveDeployPhase(streamLog, running, lastErr),
    [streamLog, running, lastErr],
  );
  const previewCacheBust = useMemo(
    () => (deployPhase === 'live' ? lastCloudflareHeadSha(streamLog) : undefined),
    [deployPhase, streamLog],
  );

  const onRunProvisioning = async () => {
    if (lead.provisioning.codeLocked) return;
    setLastErr(null);
    setStreamLog([]);
    setRunning(true);
    try {
      await provisionLeadStream(lead.id, (ev) => {
        setStreamLog((prev) => [...prev.slice(-80), ev]);
      });
    } catch (e) {
      setLastErr(e instanceof Error ? e.message : String(e));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="fp-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="rounded-full bg-nm-accent-tint px-2.5 py-1 text-xs font-bold uppercase tracking-wider text-nm-accent">
            {provisioning.status}
          </div>
          <button
            type="button"
            disabled={running || Boolean(provisioning.codeLocked)}
            onClick={() => void onRunProvisioning()}
            className="fp-btn fp-btn-primary disabled:cursor-not-allowed"
          >
            {provisioning.codeLocked ? 'Locked — live project' : running ? 'Generating…' : 'Generate preview'}
          </button>
        </div>
        <div className="mt-4">
          <PreviewLink hostname={provisioning.hostname} cacheBust={previewCacheBust} />
          <DeployPreviewStatus
            streamLog={streamLog}
            running={running}
            lastErr={lastErr}
            provisioningSteps={provisioning.steps}
          />
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3 text-xs text-nm-muted">
          <Field label="Started" value={fullDateTime(provisioning.startedAt)} />
          <Field label="Finished" value={fullDateTime(provisioning.finishedAt)} />
          <Field label="GitHub repo" value={provisioning.repo} />
          <Field label="Pages project" value={provisioning.pagesProject} />
        </div>
      </div>

      {lastErr && (
        <div className="rounded-lg border border-nm-danger/30 bg-nm-danger-bg px-4 py-3 text-sm text-nm-danger">
          {lastErr}
        </div>
      )}

      {(running || streamLog.length > 0) && (
        <div className="rounded-xl border border-nm-info/30 bg-nm-info-bg p-4">
          <div className="max-h-56 space-y-1 overflow-y-auto font-mono text-xs text-nm-ink">
            {streamLog.map((ev, i) => (
              <ProvisionStreamLine key={`${ev.step}-${ev.status}-${i}`} ev={ev} />
            ))}
          </div>
        </div>
      )}

      {provisioning.steps.length > 0 && (
        <ol className="space-y-2">
          {provisioningStepsForDisplay(provisioning.steps).map((step, i) => (
            <li key={`${step.name}-${step.startedAt ?? i}`} className="rounded-lg border border-nm-border p-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-nm-ink">{step.name}</span>
                <span className="text-[10px] font-bold uppercase text-nm-muted">{step.status}</span>
              </div>
              {step.message && <div className="mt-1 text-[12px] text-nm-ink">{step.message}</div>}
              {step.startedAt && <div className="mt-1 text-[11px] text-nm-muted">{relativeTime(step.startedAt)}</div>}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-nm-muted">{label}</div>
      <div className="mt-0.5 text-nm-ink">{value || '-'}</div>
    </div>
  );
}

const PROVISION_PHASE_ORDER = ['github', 'cloudflare', 'pipeline'];

function stepTimestamp(s: ProvisioningStep): string {
  return s.finishedAt ?? s.startedAt ?? '';
}

function provisioningStepsForDisplay(steps: ProvisioningStep[]): ProvisioningStep[] {
  const latest = new Map<string, ProvisioningStep>();
  for (const s of steps) {
    const prev = latest.get(s.name);
    if (!prev || stepTimestamp(s) >= stepTimestamp(prev)) latest.set(s.name, s);
  }
  const ordered: ProvisioningStep[] = [];
  for (const name of PROVISION_PHASE_ORDER) {
    const x = latest.get(name);
    if (x) ordered.push(x);
  }
  for (const [name, s] of latest) {
    if (!PROVISION_PHASE_ORDER.includes(name)) ordered.push(s);
  }
  return ordered;
}
